import { createAsyncAction } from 'react-redux-await-control';
import { ticket as ticketApi } from '~/services';
import { message } from 'antd';
import { CreateTicket, TicketComment } from '~/typings/entities/Ticket';
import { handleErrors } from '~/common/utils/errorHandler';
import { TicketBatchOperation } from './enums';
import { showBatchUpdateMessages } from './batchMessages';
import { parseTicketListError, reportTicketListError } from './ticketListErrorReport';

export const getServiceChannels = createAsyncAction('TICKET_GET_SERVICE_CHANNELS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.channels,
    onRequestError: () => message.error('Erro ao tentar consultar canais de atendimento'),
    request: () => ticketApi.get(`/service-channels`),
  },
});

export const getReasons = createAsyncAction('TICKET_GET_REASONS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.reasons,
    onRequestError: () => message.error('Erro ao tentar consultar motivos de ticket'),
    request: () => ticketApi.get(`/reasons/all-by-journey`),
  },
});

export const getTicketUsers = createAsyncAction('TICKET_GET_USERS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.users,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar usuários do ticket' }),
    request: () => ticketApi.get(`/users`),
  },
});

export const getTicketComments = createAsyncAction('TICKET_GET_COMMENTS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.comments,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar comentários do ticket' }),
    request: (id) => ticketApi.get(`/comments`, { params: { id } }),
  },
});

export const getCommentTypes = createAsyncAction('TICKET_GET_COMMENT_TYPES', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar tipos de comentário' }),
    request: () => ticketApi.get(`/comment-types`),
  },
});

export const getTicketsByUser = createAsyncAction('TICKET_LIST_BY_USER', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.tickets,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar tickets do usuário' }),
    request: ({ personId }) => ticketApi.get(`/tickets/by-person`, { params: { personId } }),
  },
});

export const getTicketsByCompany = createAsyncAction('TICKET_LIST_BY_COMPANY', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.tickets,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar tickets da empresa' }),
    request: ({ companyId }) => ticketApi.get(`/tickets/by-company`, { params: { companyId } }),
  },
});

export const getTicketTeams = createAsyncAction('TICKET_GET_TEAMS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.teams?.filter((team) => !!team.enabled),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar times do ticket' }),
    request: () => ticketApi.get(`/teams`),
  },
});

export const getTicketProducts = createAsyncAction('TICKET_GET_PRODUCTS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.products?.filter((product) => !!product.enabled),
    onRequestError: (err) => {
      handleErrors(err, { defaultMessage: 'Erro ao consultar produtos do ticket' });
    },
    request: () => ticketApi.get(`/products`),
  },
});

export const getTicketJourneys = createAsyncAction('TICKET_GET_JOURNEYS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.journeys?.filter((journey) => !!journey.enabled),
    onRequestError: (err) => {
      handleErrors(err, { defaultMessage: 'Erro ao consultar jornadas do ticket' });
    },
    request: () => ticketApi.get(`/journeis`),
  },
});

export const getTicketJourneysSteps = createAsyncAction('TICKET_GET_JOURNEYS_STEPS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.steps,
    onRequestError: (err) => {
      handleErrors(err, { defaultMessage: 'Erro ao consultar etapas da jornada do ticket' });
    },
    request: () => ticketApi.get(`/journeis/steps`),
  },
});

export const getTicketResolvingAreas = createAsyncAction('TICKET_GET_RESOLVING_AREAS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.areas,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar áreas resolvedoras do ticket' }),
    request: () => ticketApi.get(`/resolving-areas`),
  },
});

export const getTicketAccompaniments = createAsyncAction('TICKET_GET_ACCOMPANIMENTS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.accompaniments.reverse(),
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar historico do ticket' }),
    request: ({ id }) => ticketApi.get(`/tickets/${id}/accompaniments`),
  },
});

export const getTicketStatus = createAsyncAction('TICKET_GET_STATUS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.status,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar status do ticket' }),
    request: () => ticketApi.get(`/status`),
  },
});

export const getTicketDetails = createAsyncAction('TICKET_GET_DETAILS', {
  initialValue: null,
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar detalhes do ticket' }),
    request: ({ id }) => ticketApi.get(`/tickets/${id}`),
  },
});

export const getTicketResolutionReasons = createAsyncAction('TICKET_GET_RESOLUTION_REASONS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.justifications,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar motivos de resolução do ticket' }),
    request: () => ticketApi.get(`/justifications`),
  },
});

export const createTicket = createAsyncAction('TICKET_CREATE', {
  initialValue: {},
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: ({ response }) => response?.data,
    onRequestError: (err) => {
      if (err?.response?.status !== 409) {
        handleErrors(err, { defaultMessage: 'Erro ao tentar criar um ticket' });
      }
    },
    onRequestSuccess: (r: any) => {
      const msg = r?.slaMessageValue
        ? `Ticket #${r?.id} criado com sucesso. Resolução em até ${r?.slaMessageValue} ${r?.slaMessageDescription}.`
        : `Ticket #${r?.id} criado com sucesso`;

      message.success(msg, 6);
    },
    request: ({ ticket }: { ticket: CreateTicket }) => ticketApi.post(`/tickets`, ticket),
  },
});

export const createTicketComment = createAsyncAction('TICKET_CREATE_COMMENT', {
  context: {
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao tentar criar um comentário' }),
    onRequestSuccess: () => message.success('Comentário criado com sucesso'),
    request: (data: TicketComment) => ticketApi.post(`/comments`, data),
  },
});

export const updateTicketComment = createAsyncAction('TICKET_UPDATE_COMMENT', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao tentar atualizar um comentário' }),
    onRequestSuccess: () => message.success('Comentário atualizado com sucesso'),
    request: ({ id, ...data }) => ticketApi.put(`/comments/${id}/update`, data),
  },
});

export const updateTicket = createAsyncAction('TICKET_UPDATE', {
  // o resultado (ticket atualizado) alimenta o watcher da tela de atendimento
  // (remove da fila/fecha card quando o status sai do escopo)
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao atualizar o ticket' }),
    onRequestSuccess: () => message.success('Ticket atualizado com sucesso'),
    request: ({ ticketId, data }) => {
      const formData = new FormData();
      const files = data?.files;
      const hasFiles = Array.isArray(files) && files.length > 0;
      const ticket = { ...(data || {}) };
      delete ticket.files;
      delete ticket.comment;

      formData.append('ticket', JSON.stringify(ticket));

      if (hasFiles) {
        files.forEach((file) => {
          if (file?.file) {
            formData.append('files', file.file);
          } else {
            formData.append('files', file);
          }
        });
      }

      // The backend reads the `comment` part as a list of comments (CommentRequestDTO[]).
      // Reclame Aqui resolutions send multiple typed messages (WhatsApp, public and reserved),
      // while other channels send a single comment; normalize both to an array.
      if (data?.comment) {
        const comments = Array.isArray(data.comment) ? data.comment : [data.comment];
        if (comments.length) {
          formData.append('comment', JSON.stringify(comments));
        }
      }

      return ticketApi.put(`/tickets/${ticketId}/updateWithFiles`, formData);
    },
  },
});

export const hasLinkedTicket = createAsyncAction('HAS_TICKET_LINKED', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    request: (data) => ticketApi.post(`/tickets/has-link`, data),
  },
});

export const selectTicket = createAsyncAction('SELECT_TICKET', {
  saveResult: true,
  context: {
    responseParser: (res: any) => res,
    request: (data) => data,
  },
});

export const selectAssessment = createAsyncAction('SELECT_TICKET_ASSESSMENT', {
  saveResult: true,
  context: {
    responseParser: (res: any) => res,
    request: (data) => data,
  },
});

export const getTickets = createAsyncAction('GET_TICKETS', {
  saveResult: true,
  initialValue: {},
  context: {
    responseParser: ({ data: { resource, ...rest } }) => ({
      tickets: resource,
      ...rest,
    }),
    onRequestError: () => {},
    request: (params) => ticketApi.get(`/tickets`, { params }),
  },
});

export const getFlatTickets = createAsyncAction('GET_FLAT_TICKETS', {
  saveResult: true,
  initialValue: {},
  context: {
    responseParser: ({ data: { resource, ...rest } }) => ({
      tickets: resource?.map((el) => el?.ticket),
      ...rest,
    }),
    // CORE-4167: a falha vira `{ error: { status, developerMessage } }` no result (a tela
    // escolhe a mensagem pelo status) e vai ao Sentry com contexto; sem toast, a fila
    // mostra o erro inline com "Tentar novamente".
    errorParser: parseTicketListError,
    onRequestError: (err, params, meta) => reportTicketListError(err, params, meta),
    request: (params) => ticketApi.get(`/tickets`, { params }),
  },
});

export const getExperts = createAsyncAction('GET_TICKETS_EXPERTS', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data.resource,
    onRequestError: () => {},
    request: () => ticketApi.get(`/tickets/experts`),
  },
});

export const createTicketAssessment = createAsyncAction('CREATE_TICKET_ASSESSMENT', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    request: ({ ticketId, ...data }) => ticketApi.post(`/tickets/${ticketId}/assessments`, data),
  },
});

export const updateTicketAssessment = createAsyncAction('UPDATE_TICKET_ASSESSMENT', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    request: ({ ticketId, ...data }) => ticketApi.put(`/tickets/${ticketId}/assessments/${data.id}`, data),
  },
});

export const getTicketMessages = createAsyncAction('GET_TICKETS_MESSAGES', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Falha ao carregar novas mensagens'),
    request: ({ conversationId, pageToken, pageSize = 10 }) =>
      ticketApi.get(`/conversations/${conversationId}/messages`, {
        params: {
          pageSize,
          pageToken,
        },
      }),
  },
});

export const getConciergeMessages = createAsyncAction('GET_CONCIERGE_MESSAGES', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Falha ao carregar mensagens do concierge'),
    request: ({ conversationId, cursor, pageSize = 10 }) =>
      ticketApi.get(`/conversations/${conversationId}/messages/history`, {
        params: {
          limit: pageSize,
          ...(cursor ? { cursor } : {}),
        },
      }),
  },
});

export const getTicketMedia = createAsyncAction('GET_TICKETS_MEDIA', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    request: ({ conversationId, sid }) => ticketApi.get(`/conversations/${conversationId}/media/${sid}`),
  },
});

export const getTicketAssessment = createAsyncAction('GET_TICKETS_ASSESSMENT', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data.assessments,
    onRequestError: () => {},
    request: ({ ticketId }) => ticketApi.get(`/tickets/${ticketId}/assessments`),
  },
});

export const getTicketResponsibles = createAsyncAction('GET_TICKET_RESPONSIBLES', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    request: ({ page, size, sort, responsibleName }) =>
      ticketApi.get(`/responsibles`, {
        params: {
          page,
          size,
          sort,
          responsibleName,
        },
      }),
  },
});

export const updateResponsible = createAsyncAction('UPDATE_TICKET_RESPONSIBLE', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    onRequestSuccess: () => message.success('Ticket alterado com sucesso'),
    request: (data) => ticketApi.put(`/responsibles/update`, data),
  },
});

export const updateBatchTicket = createAsyncAction('UPDATE_BATCH_TICKET', {
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: () => {},
    onRequestSuccess: (data, payload) => {
      showBatchUpdateMessages(data, payload);
    },
    request: (data) => {
      const { operation, ...payload } = data || {};
      delete payload?.resolvingArea;
      if (operation === TicketBatchOperation.RESPONSIBLE) {
        return ticketApi.put('/responsibles/batch-update', payload);
      }

      return ticketApi.post('/tickets/batch/update', payload);
    },
  },
});

export const removeResponsible = createAsyncAction('DELETE_TICKET_RESPONSIBLE', {
  context: {
    onRequestError: () => {},
    request: (data) => ticketApi.delete(`/responsibles/delete`, { data }),
  },
});

export const getTicketStatusGroup = createAsyncAction('TICKET_GET_STATUS_GROUP', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.groupStatus,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao consultar grupo de status do ticket' }),
    request: () => ticketApi.get(`/status/groups`),
  },
});

export const getTicketReasons = createAsyncAction('GET_TICKET_REASONS', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data.reasons,
    onRequestError: () => message.error('Erro ao tentar consultar motivos de ticket'),
    request: () => ticketApi.get(`/reasons`),
  },
});

export const getTicketSupportService = createAsyncAction('GET_TICKET_SUPPORT_SERVICE', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Erro ao tentar consultar atendimentos'),
    request: ({ id }) => ticketApi.get(`/${id}/support-service`),
  },
});

export const createTicketSupportService = createAsyncAction('CREATE_TICKET_SUPPORT_SERVICE', {
  initialValue: [],
  saveResult: true,
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Erro ao tentar criar atendimentos'),
    request: ({ id, data }) => ticketApi.post(`/${id}/support-service`, data),
  },
});

export const createTicketFiles = createAsyncAction('TICKET_CREATE_FILES', {
  initialValue: {},
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao tentar criar arquivos do ticket' }),
    onRequestSuccess: () => {},
    request: ({ ticketId, files, createdById, createdBy, personId }) => {
      const formData = new FormData();

      formData.append('createdById', createdById);
      formData.append('createdBy', createdBy);

      if (personId) {
        formData.append('personId', personId);
      }

      if (files?.length > 0) {
        files.forEach((file) => {
          if (file.file) {
            formData.append('files', file.file);
          } else {
            formData.append('files', file);
          }
        });
      }
      return ticketApi.post(`/${ticketId}/files`, formData);
    },
  },
});

export const getTicketFiles = createAsyncAction('TICKET_GET_FILES', {
  initialValue: [],
  saveResult: true,
  context: {
    responseParser: ({ data }) => data?.files || [],
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao obter arquivos do ticket' }),
    request: ({ ticketId }) => ticketApi.get(`/${ticketId}/files`),
  },
});

export const getBatchImportTemplate = createAsyncAction('TICKET_GET_BATCH_IMPORT_TEMPLATE', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => null,
    onRequestError: () => message.error('Erro ao baixar modelo da planilha'),
    request: () => ticketApi.get('/tickets/batch/update/import/template'),
  },
});

export const importBatchExecution = createAsyncAction('TICKET_IMPORT_BATCH_EXECUTION', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => null,
    onRequestError: () => message.error('Erro ao importar planilha'),
    onRequestSuccess: () => message.success('Planilha importada com sucesso'),
    request: ({ file, userCreated, userCreatedId }) => {
      const formData = new FormData();
      formData.append('file', file);
      return ticketApi.post('/tickets/batch/update/import/executions', formData, {
        params: { userCreated, userCreatedId },
      });
    },
  },
});

export const getBatchExecutions = createAsyncAction('TICKET_GET_BATCH_EXECUTIONS', {
  initialValue: { executions: [] },
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => ({ executions: [] }),
    onRequestError: () => message.error('Erro ao consultar execuções de importação'),
    request: () => ticketApi.get('/tickets/batch/update/import/executions'),
  },
});

export const getBatchCreationTemplate = createAsyncAction('TICKET_GET_BATCH_CREATION_TEMPLATE', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => null,
    onRequestError: () => message.error('Erro ao baixar modelo da planilha'),
    request: () => ticketApi.get('/creation-batch/template'),
  },
});

export const importBatchCreationExecution = createAsyncAction('TICKET_IMPORT_BATCH_CREATION_EXECUTION', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => null,
    onRequestError: () => message.error('Erro ao importar planilha'),
    request: ({ file, authorName, authorId }) => {
      const formData = new FormData();
      formData.append('file', file);
      return ticketApi.post('/creation-batch/executions', formData, {
        params: { authorName, authorId },
      });
    },
  },
});

export const getBatchCreationExecutions = createAsyncAction('TICKET_GET_BATCH_CREATION_EXECUTIONS', {
  initialValue: { executions: [] },
  saveResult: true,
  context: {
    responseParser: ({ data }) => (Array.isArray(data) ? { executions: data } : data),
    errorParser: () => ({ executions: [] }),
    onRequestError: () => message.error('Erro ao consultar execuções de criação'),
    request: () => ticketApi.get('/creation-batch/executions'),
  },
});

export const getTicketDistributionConfig = createAsyncAction('TICKET_GET_DISTRIBUTION_CONFIG', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => [],
    onRequestError: () => {},
    request: ({ id }) => ticketApi.get(`/users/${id}`),
  },
});

export const updateTicketDistributionFlag = createAsyncAction('TICKET_UPDATE_DISTRIBUTION_FLAG', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao atualizar o status de distribuição' }),
    request: ({ id, enabled, optionId, loggedUser }) =>
      ticketApi.patch(`/users/${id}/distribution-flag`, {
        enabled,
        ...(optionId !== undefined && { optionId }),
        loggedUser,
      }),
  },
});

export const getTicketCategories = createAsyncAction('TICKET_GET_CATEGORIES', {
  initialValue: [],
  saveResult: true,
  context: {
    responseParser: ({ data }) => data?.categories || [],
    errorParser: () => [],
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao obter categorias do ticket' }),
    request: () => ticketApi.get(`/categories`),
  },
});

export const getTicketClassificationRules = createAsyncAction('TICKET_GET_CLASSIFICATION_RULES', {
  initialValue: [],
  saveResult: true,
  context: {
    responseParser: ({ data }) => data?.rules || [],
    errorParser: () => [],
    onRequestError: () => {},
    request: () => ticketApi.get(`/classification-rules`),
  },
});

export const getTicketDashboard = createAsyncAction('GET_TICKET_DASHBOARD', {
  initialValue: {},
  saveResult: true,
  context: {
    errorParser: () => ({}),
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Erro ao consultar dashboard de tickets'),
    request: (params) => ticketApi.get('/tickets/dashboard', { params }),
  },
});

export const getTicketDashboardAgents = createAsyncAction('GET_TICKET_DASHBOARD_AGENTS', {
  initialValue: {},
  saveResult: true,
  context: {
    errorParser: () => ({}),
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Erro ao consultar dashboard de agentes'),
    request: (params) => ticketApi.get('/tickets/dashboard/agents', { params }),
  },
});

export const exportTickets = createAsyncAction('TICKET_EXPORT', {
  context: {
    onRequestError: (err) => handleErrors(err, { defaultMessage: 'Erro ao iniciar exportação' }),
    onRequestSuccess: () => message.success('Exportação iniciada em segundo plano'),
    request: (params: any) => {
      const { userCreated, userCreatedId, ...body } = params || {};
      return ticketApi.post('/tickets/export', body, {
        params: { userCreated, userCreatedId },
      });
    },
    responseParser: ({ data }) => data,
  },
});

export const getExportStatus = createAsyncAction('TICKET_GET_EXPORT_STATUS', {
  context: {
    request: ({ jobId, userId }: { jobId: string; userId?: number | string | null }) =>
      ticketApi.get(`/tickets/export/${jobId}`, {
        params: userId != null ? { userCreatedId: userId } : undefined,
      }),
    responseParser: ({ data }) => data,
    errorParser: (err: any) => ({ status: err?.response?.status ?? null }),
  },
});

export const RESUME_EXPORT_POLLING = 'TICKET_RESUME_EXPORT_POLLING' as const;
export const EXPORT_PENDING_JOB_KEY = 'ticket-export-pending-job-id' as const;
